import LanguageFlag from './LanguageFlag';

// Queue-mode presentation of one PC: a horizontal bar instead of a tile
// (design/BarAnatomy.dc.html). Deliberately DUMB — props in, markup out, no
// hooks, no polling, no agent calls. PCTile (and FakePCTile) own the agent
// poll, the launch flow, the dispatch registration and the recovery actions;
// they render this with the values they have already computed.
//
// Colour says only four things (design/BarAnatomy.dc.html):
//   green  #00ff88  loopt
//   amber  #ff9800  bijna klaar, bril klaarmaken
//   red    #ff4444  er is iets mis
//   cyan   #00d4ff  vrij of aan het laden
// Anything else (offline) is simply dimmed.

export type PCBarState =
  | 'running'   // green
  | 'nearly'    // amber — the loudest normal state
  | 'problem'   // red — promotes in place with the message + recovery buttons
  | 'loading'   // cyan
  | 'free'      // dashed cyan (next visitor known) / dashed grey (nobody waiting)
  | 'offline';  // dimmed

export interface PCBarAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  /** 'go' = the green Start affordance, 'quiet' = outline only. */
  tone?: 'blue' | 'neutral' | 'go' | 'quiet';
}

/**
 * An exception worth seeing without opening the sheet: a low or uncharged
 * headset, a sync that is downloading or left paused, checks that block a
 * launch. ABNORMAL ONLY — a healthy bar carries none.
 *
 * `tone` stays inside the bar's four-colour vocabulary and adds no fifth
 * meaning: 'problem' = red (er is iets mis), 'nearly' = amber (aandacht nodig
 * voor de volgende bezoeker), 'loading' = cyan (bezig). There is deliberately
 * no green badge: green means running, and a running PC needs no badge.
 */
export interface PCBarBadge {
  key: string;
  label: string;
  tone: 'problem' | 'nearly' | 'loading';
  title?: string;
}

/** One device readout on the left of the bar. `pct` null = no reading (device
 *  not connected or an older agent), which reads as a dash rather than 0%. */
export interface PCBarDevice {
  key: 'hmd' | 'audio';
  pct: number | null;
  /** true = charging, false = discharging, null/undefined = no reading. Shown
   *  as an arrow next to the percentage: losing charge is the thing staff need
   *  to catch, and a number alone does not say which way it is heading. */
  charging?: boolean | null;
  tone: 'ok' | 'warn' | 'bad';
  title: string;
}

/* Inline SVG rather than emoji: Windows renders a lot of emoji as flat letter
   boxes in this app's font stack, which is what made the language flags look
   "schraal" earlier. */
function DeviceIcon({ kind }: { kind: 'hmd' | 'audio' }) {
  return kind === 'hmd' ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 9.5A1.5 1.5 0 0 1 4.5 8h15A1.5 1.5 0 0 1 21 9.5v4a1.5 1.5 0 0 1-1.5 1.5h-4l-1.2-1.4a2 2 0 0 0-3 0L10 15H4.5A1.5 1.5 0 0 1 3 13.5z" />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
      <rect x="2.5" y="13.5" width="4" height="6" rx="1.4" />
      <rect x="17.5" y="13.5" width="4" height="6" rx="1.4" />
    </svg>
  );
}

export interface PCBarVisitor {
  name: string;
  /** Lowercase 2-letter play language (LanguageFlag renders nothing for the rest). */
  language?: string | null;
  /** "2 van 3" for a party member, '' for a solo visitor. */
  party?: string;
  experienceName?: string | null;
}

export interface PCBarViewProps {
  pcName: string;
  /** The label is a bare player index (operator view), so the column narrows. */
  compactPc?: boolean;
  /** Headset and headphones, ALWAYS shown rather than only on exception: the
   *  charge level is something staff plan the next hour around, not just an
   *  alarm (Dirk 2026-09-12). Rendered next to the player index because that is
   *  where the eye already is. */
  devices?: PCBarDevice[];
  state: PCBarState;
  /** Who is playing here right now (running / nearly / loading / problem). */
  visitor?: PCBarVisitor | null;
  /** Free PC: who the queue says lands here next. */
  nextVisitor?: PCBarVisitor | null;
  /** The experience on this PC when there is no queue visitor to carry it. */
  experienceName?: string | null;
  /** Language the RUN was started in, for a walk-in or test launch that has no
   *  queue visitor to carry one. The visitor's own language still wins. */
  runLanguage?: string | null;
  /** 0..1 track fill. null hides the track row. */
  progress?: number | null;
  /** Text to the right of the track ("Laden…", "Bijna klaar"). */
  statusText?: string | null;
  /** Running visitor: where they are ("1/11: BasecampSpawn"). Rendered in the
   *  same fixed-width block as statusText, so the track keeps ONE width in
   *  every bar — a track that grew when the scene name was short made two
   *  equally-far-along PCs look different (Dirk 2026-09-16). */
  statusScene?: string | null;
  /** Time already played — the number staff act on, so it leads and stays
   *  legible; the estimate after it is the softer one. */
  statusElapsed?: string | null;
  /** Estimated time still to play, already formatted ("nog ~9 min"). */
  statusRemaining?: string | null;
  statusRemainingTitle?: string;
  /** Red headline for the problem state. */
  problemText?: string | null;
  /** Inline recovery buttons, problem state only. */
  recoveryActions?: PCBarAction[];
  /** Free / offline right-hand action (Start, Zet aan). */
  primaryAction?: PCBarAction | null;
  /** Free/offline centre text when there is nobody to name ("Vrij"). */
  idleText?: string | null;
  /** Exception badges (abnormal only), between the content and the buttons. */
  badges?: PCBarBadge[];
  /** Icon buttons left of Ingrijpen. Only passed while somebody is in there. */
  quickActions?: PCBarQuickAction[];
  /** Opens the intervene panel. Omit to hide the button. */
  onIntervene?: (() => void) | null;
  interveneTitle?: string;
  /** Tighter row for rooms with many PCs (design: "dichte variant"). */
  dense?: boolean;
  errorText?: string | null;
}

/** One icon button on the bar: something staff reach for MID-RUN, often enough
 *  that opening the intervene sheet for it is friction. Deliberately a short
 *  list — every icon added here costs the others some recognisability. */
export interface PCBarQuickAction {
  key: 'message' | 'cast';
  onClick: () => void;
  disabled?: boolean;
  title: string;
  /** 'alert' = red (the envelope, which interrupts a visitor), 'info' = the
   *  same blue HeadsetSheet already uses for Live meekijken. */
  tone?: 'alert' | 'info' | 'neutral';
}

function QuickIcon({ kind }: { kind: PCBarQuickAction['key'] }) {
  return kind === 'message' ? (
    // Envelope — "bericht aan bezoeker".
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 6.5l8.5 6 8.5-6" />
    </svg>
  ) : (
    // Camera — "live meekijken".
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h9A1.5 1.5 0 0 1 15 8.5v7A1.5 1.5 0 0 1 13.5 17h-9A1.5 1.5 0 0 1 3 15.5z" />
      <path d="M15 11l5-2.5v7L15 13z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M12 8v5" />
      <path d="M12 17h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="qbar-arrow" aria-hidden="true">
      <path d="M5 12h13" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

function actionClass(a: PCBarAction): string {
  switch (a.tone) {
    case 'go': return 'qbar-btn qbar-btn--go';
    case 'quiet': return 'qbar-btn qbar-btn--quiet';
    case 'neutral': return 'qbar-btn qbar-btn--neutral';
    default: return 'qbar-btn qbar-btn--blue';
  }
}

function VisitorLine({ v, fallbackExperience }: { v: PCBarVisitor; fallbackExperience?: string | null }) {
  const experience = v.experienceName ?? fallbackExperience ?? null;
  return (
    <div className="qbar-line">
      <span className="qbar-visitor">{v.name}</span>
      <LanguageFlag code={v.language} className="qbar-flag" />
      {v.party ? <span className="qbar-party">{v.party}</span> : null}
      {/* Next to the name rather than flung to the far edge (Dirk 2026-09-16):
          who is playing and what they are playing are one fact, and reading
          them meant crossing the whole bar. */}
      {experience ? <span className="qbar-exp">{experience}</span> : null}
      <span className="qbar-spacer" />
    </div>
  );
}

export default function PCBarView({
  pcName,
  compactPc,
  devices,
  state,
  visitor,
  nextVisitor,
  experienceName,
  runLanguage,
  progress,
  statusText,
  statusScene,
  statusElapsed,
  statusRemaining,
  statusRemainingTitle,
  problemText,
  recoveryActions,
  primaryAction,
  idleText,
  badges,
  quickActions,
  onIntervene,
  interveneTitle,
  dense = false,
  errorText,
}: PCBarViewProps) {
  const isFree = state === 'free';
  const freeWithNext = isFree && !!nextVisitor;
  const className = [
    'qbar',
    `qbar--${state}`,
    freeWithNext ? 'qbar--free-next' : '',
    dense ? 'qbar--dense' : '',
  ].filter(Boolean).join(' ');

  const pcBlock = (
    <div className={`qbar-pc${compactPc ? ' qbar-pc--compact' : ''}`}>
      <span className={`qbar-dot${isFree || state === 'offline' ? ' qbar-dot--hollow' : ''}`} aria-hidden="true" />
      <span className="qbar-pc-name" title={pcName}>{pcName}</span>
    </div>
  );

  const deviceBlock = devices && devices.length > 0 ? (
    <div className="qbar-devices">
      {devices.map((d) => (
        <span key={d.key} className={`qbar-dev qbar-dev--${d.tone}`} title={d.title}>
          <DeviceIcon kind={d.key} />
          <span className="qbar-dev-pct">{d.pct === null ? '--' : `${d.pct}%`}</span>
          {d.charging === true ? (
            <span className="qbar-dev-arrow" aria-hidden="true">↑</span>
          ) : d.charging === false ? (
            <span className="qbar-dev-arrow" aria-hidden="true">↓</span>
          ) : null}
        </span>
      ))}
    </div>
  ) : null;

  const badgeRow = badges && badges.length > 0 ? (
    <div className="qbar-badges">
      {badges.map((b) => (
        <span key={b.key} className={`qbar-badge qbar-badge--${b.tone}`} title={b.title}>
          {b.label}
        </span>
      ))}
    </div>
  ) : null;

  const quickBtns = quickActions && quickActions.length > 0 ? (
    <div className="qbar-quick">
      {quickActions.map((a) => (
        <button
          key={a.key}
          type="button"
          className={`qbar-quick-btn${a.tone && a.tone !== 'neutral' ? ` qbar-quick-btn--${a.tone}` : ''}`}
          onClick={a.onClick}
          disabled={a.disabled}
          title={a.title}
          aria-label={a.title}
        >
          <QuickIcon kind={a.key} />
        </button>
      ))}
    </div>
  ) : null;

  const interveneBtn = onIntervene ? (
    <button
      type="button"
      className="qbar-intervene"
      onClick={onIntervene}
      title={interveneTitle || 'Er is iets: open de opties voor deze PC en bezoeker'}
      aria-label="Ingrijpen"
    >
      <InfoIcon />
    </button>
  ) : null;

  const primaryBtn = primaryAction ? (
    <button
      type="button"
      className={actionClass(primaryAction)}
      onClick={primaryAction.onClick}
      disabled={primaryAction.disabled}
      title={primaryAction.title}
    >
      {primaryAction.label}
    </button>
  ) : null;

  // --- Problem: the exception. Promotes IN PLACE — the bar grows a message
  // line and the recovery buttons, rather than sending the operator elsewhere.
  if (state === 'problem') {
    return (
      <div className={className}>
        <div className="qbar-row">
          {pcBlock}
          {deviceBlock}
          <div className="qbar-main">
            {visitor ? (
              <VisitorLine v={visitor} fallbackExperience={experienceName} />
            ) : experienceName ? (
              <div className="qbar-line">
                <span className="qbar-spacer" />
                <span className="qbar-exp">{experienceName}</span>
              </div>
            ) : null}
            <div className="qbar-problem">{problemText}</div>
          </div>
          {badgeRow}
          {quickBtns}
          {interveneBtn}
        </div>
        {recoveryActions && recoveryActions.length > 0 ? (
          <div className="qbar-actions">
            {recoveryActions.map((a) => (
              <button
                key={a.label}
                type="button"
                className={actionClass(a)}
                onClick={a.onClick}
                disabled={a.disabled}
                title={a.title}
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
        {errorText ? <div className="qbar-error">{errorText}</div> : null}
      </div>
    );
  }

  // --- Free / offline: one line, one action.
  if (isFree || state === 'offline') {
    return (
      <div className={className}>
        {pcBlock}
        {deviceBlock}
        {freeWithNext && nextVisitor ? (
          <div className="qbar-main qbar-main--inline">
            <span className="qbar-idle">{idleText || 'Vrij'}</span>
            <ArrowIcon />
            <span className="qbar-visitor">{nextVisitor.name}</span>
            <LanguageFlag code={nextVisitor.language} className="qbar-flag" />
            {nextVisitor.experienceName ? (
              <span className="qbar-exp">{nextVisitor.experienceName}</span>
            ) : null}
          </div>
        ) : (
          <div className="qbar-main qbar-main--inline">
            <span className="qbar-idle">{idleText || 'Vrij'}</span>
          </div>
        )}
        {badgeRow}
        {primaryBtn}
        {interveneBtn}
        {errorText ? <div className="qbar-error">{errorText}</div> : null}
      </div>
    );
  }

  // The status block is FIXED WIDTH (see .qbar-status in queue.css): the track
  // takes the rest, so it ends at the same x in every bar no matter how long
  // the scene name is. Inside it the scene ellipsises and the clock never does.
  const statusBlock =
    statusScene || statusElapsed || statusRemaining ? (
      <span className="qbar-status">
        {statusScene ? <span className="qbar-status-scene">{statusScene}</span> : null}
        {statusElapsed || statusRemaining ? (
          <span className="qbar-status-clock">
            {statusElapsed ? <span className="qbar-status-elapsed">{statusElapsed}</span> : null}
            {statusRemaining ? (
              <span className="qbar-status-remaining" title={statusRemainingTitle}>
                {statusRemaining}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
    ) : statusText ? (
      <span className="qbar-status">{statusText}</span>
    ) : null;

  // --- Running / nearly done / loading.
  //
  // Who is playing gets its OWN column, stacked and ordered by how much it
  // actually decides (Dirk 2026-09-16): flag, then experience, then the name.
  // The name is last because it is the one thing staff rarely act on — they
  // need to know WHAT is running and in which language long before they need
  // to know whose name is on it. The progress bar sits to the right of that
  // column rather than under it, so the row reads left to right: who → how far.
  const runExperience = visitor?.experienceName ?? experienceName ?? null;
  // A booked visitor's language wins; a walk-in or test launch falls back to
  // what the run itself was started in, so the flag is never simply missing on
  // a running experience (Dirk 2026-09-16).
  const runFlag = visitor?.language ?? runLanguage ?? null;
  const whoBlock = (
    <div className="qbar-who">
      <div className="qbar-who-head">
        <LanguageFlag code={runFlag} className="qbar-flag" />
        {runExperience ? <span className="qbar-exp">{runExperience}</span> : null}
      </div>
      <div className="qbar-who-name">
        {visitor ? (
          <>
            <span className="qbar-visitor">{visitor.name}</span>
            {visitor.party ? <span className="qbar-party">{visitor.party}</span> : null}
          </>
        ) : (
          <span className="qbar-idle">{idleText || 'Bezig'}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className={className}>
      {pcBlock}
      {deviceBlock}
      <div className="qbar-main qbar-main--run">
        {whoBlock}
        {progress !== null && progress !== undefined ? (
          // Clock and scene sit ABOVE the bar (Dirk 2026-09-16): the numbers are
          // read far more often than the bar is, so they get the top line and
          // the bar becomes the thing you glance at underneath them.
          <div className="qbar-track-row">
            {statusBlock}
            <div className="qbar-track">
              <div
                className="qbar-fill"
                style={{ width: `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%` }}
              />
            </div>
          </div>
        ) : statusBlock ? (
          <div className="qbar-track-row">{statusBlock}</div>
        ) : null}
      </div>
      {badgeRow}
      {primaryBtn}
      {quickBtns}
      {interveneBtn}
      {errorText ? <div className="qbar-error">{errorText}</div> : null}
    </div>
  );
}
